import { validateEpisodeShareResult } from "./episodeShareResult";

export const API_URL = "https://urbanpodcasts-backend-api.azurewebsites.net";
const REMEMBER_2FA_TOKEN_KEY = "remember_2fa_token";

/* ============================================================
   TOKEN + AUTH HANDLING
============================================================ */

function getToken() {
    return localStorage.getItem("token");
}

function getOptionalAuthHeaders() {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export function getRememberedTwoFactorToken() {
    return String(localStorage.getItem(REMEMBER_2FA_TOKEN_KEY) || "").trim();
}

export function setRememberedTwoFactorToken(token) {
    const value = String(token || "").trim();
    if (!value) {
        localStorage.removeItem(REMEMBER_2FA_TOKEN_KEY);
        return;
    }
    localStorage.setItem(REMEMBER_2FA_TOKEN_KEY, value);
}

function handleAuthFailure() {
    localStorage.removeItem("token");
    window.location.href = "/"; // Redirect to login
}

/**
 * apiFetch — wrapper around fetch:
 *  - Automatically includes Authorization header
 *  - Supports JSON and FormData
 *  - Auto-handles expired tokens (401/403)
 */
async function apiFetch(path, options = {}) {
    const token = getToken();
    const { skipAuthFailureRedirect = false, ...requestOptions } = options;

    const headers = { ...(requestOptions.headers || {}) };

    // Only requests with a JSON body need this header. Adding it to GET requests
    // forces otherwise-public cross-origin reads through an unnecessary preflight.
    const hasRequestBody = requestOptions.body !== undefined && requestOptions.body !== null;
    if (hasRequestBody && !(requestOptions.body instanceof FormData)) {
        headers["Content-Type"] = headers["Content-Type"] || "application/json";
    }

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(`${API_URL}${path}`, {
        ...requestOptions,
        headers,
    });

    // Try decoding JSON
    const data = await res.json().catch(() => ({}));

    // ----- Auto logout on expired token -----
    if (!skipAuthFailureRedirect && (res.status === 401 || data?.message === "Invalid or expired token")) {
        handleAuthFailure();
        return { ok: false, message: "Invalid or expired token" };
    }

    return data;
}

/**
 * apiUpload — wrapper around XMLHttpRequest for FormData uploads:
 *  - Includes Authorization header
 *  - Emits upload progress callbacks
 *  - Auto-handles expired tokens (401 or invalid token message)
 */
function apiUpload(path, formData, { onProgress } = {}) {
    return new Promise((resolve) => {
        const token = getToken();
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_URL}${path}`, true);
        if (token) {
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }

        if (xhr.upload && typeof onProgress === "function") {
            xhr.upload.onprogress = (event) => {
                const total = Number(event?.total || 0);
                const loaded = Number(event?.loaded || 0);
                const ratio = total > 0 ? loaded / total : 0;
                onProgress({
                    loaded,
                    total,
                    ratio: Number.isFinite(ratio) ? ratio : 0,
                    lengthComputable: Boolean(event?.lengthComputable),
                });
            };
        }

        xhr.onload = () => {
            const status = Number(xhr.status || 0);
            const raw = typeof xhr.responseText === "string" ? xhr.responseText : "";
            let data = {};
            try {
                data = raw ? JSON.parse(raw || "{}") : {};
            } catch {
                data = {};
            }

            if (status === 401 || data?.message === "Invalid or expired token") {
                handleAuthFailure();
                resolve({ ok: false, message: "Invalid or expired token" });
                return;
            }

            resolve(data && typeof data === "object" ? data : {});
        };

        xhr.onerror = () => {
            resolve({ ok: false, message: "Network error" });
        };

        xhr.onabort = () => {
            resolve({ ok: false, message: "Upload cancelled" });
        };

        try {
            xhr.send(formData);
        } catch (err) {
            resolve({ ok: false, message: err?.message || "Upload failed" });
        }
    }).catch((err) => ({ ok: false, message: err?.message || "Upload failed" }));
}

const LARGE_SHAREPOINT_UPLOAD_THRESHOLD_BYTES = 200 * 1024 * 1024;
const SHAREPOINT_UPLOAD_CHUNK_BYTES = 10 * 1024 * 1024;
const SHAREPOINT_UPLOAD_MAX_RETRIES = 5;

function getUploadSessionOffset(sessionJson, fallbackOffset) {
    const ranges = Array.isArray(sessionJson?.nextExpectedRanges)
        ? sessionJson.nextExpectedRanges
        : [];
    const firstRange = String(ranges[0] || "").trim();
    const match = firstRange.match(/^(\d+)/);
    if (!match) return fallbackOffset;

    const offset = Number(match[1]);
    return Number.isSafeInteger(offset) && offset >= 0 ? offset : fallbackOffset;
}

function parseUploadSessionResponse(xhr) {
    const raw = typeof xhr.responseText === "string" ? xhr.responseText : "";
    try {
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function uploadSharePointChunk(
    uploadUrl,
    file,
    start,
    endExclusive,
    totalBytes,
    onProgress
) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl, true);
        xhr.setRequestHeader(
            "Content-Range",
            `bytes ${start}-${endExclusive - 1}/${totalBytes}`
        );
        xhr.setRequestHeader("Content-Type", "application/octet-stream");

        if (xhr.upload && typeof onProgress === "function") {
            xhr.upload.onprogress = (event) => {
                const loaded = Math.min(
                    totalBytes,
                    start + Math.max(0, Number(event?.loaded || 0))
                );
                onProgress({
                    loaded,
                    total: totalBytes,
                    ratio: totalBytes > 0 ? loaded / totalBytes : 0,
                    lengthComputable: Boolean(event?.lengthComputable),
                });
            };
        }

        xhr.onload = () => {
            const status = Number(xhr.status || 0);
            const data = parseUploadSessionResponse(xhr);
            if (status >= 200 && status < 300) {
                resolve({ status, data });
                return;
            }

            const error = new Error(
                data?.error?.message || `SharePoint upload failed (${status || "network"})`
            );
            error.status = status;
            error.response = data;
            reject(error);
        };

        xhr.onerror = () => {
            const error = new Error("Network error while uploading to SharePoint");
            error.status = 0;
            reject(error);
        };

        try {
            xhr.send(file.slice(start, endExclusive));
        } catch (err) {
            err.status = Number(err?.status || 0);
            reject(err);
        }
    });
}

async function getSharePointUploadSessionState(uploadUrl) {
    const res = await fetch(uploadUrl);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const error = new Error(
            data?.error?.message || `Failed to resume SharePoint upload (${res.status})`
        );
        error.status = res.status;
        error.response = data;
        throw error;
    }
    return data;
}

function isRetryableSharePointUploadError(error) {
    const status = Number(error?.status || 0);
    return !status || status === 408 || status === 409 || status === 416 || status === 429 || status >= 500;
}

function waitForSharePointUploadRetry(attempt) {
    const delayMs = Math.min(10_000, 1_000 * 2 ** Math.max(0, attempt));
    return new Promise((resolve) => window.setTimeout(resolve, delayMs));
}

async function uploadLargeSharePointFile({
    createSessionPath,
    finalizePath,
    path,
    file,
    conflictBehavior = "rename",
    onProgress,
}) {
    const totalBytes = Number(file?.size || 0);
    if (!Number.isSafeInteger(totalBytes) || totalBytes <= 0) {
        return { ok: false, message: "The selected file is empty or invalid" };
    }

    const sessionResponse = await apiFetch(createSessionPath, {
        method: "POST",
        body: JSON.stringify({
            path: path || "/",
            file_name: file.name || "file",
            file_size: totalBytes,
            mime_type: file.type || "application/octet-stream",
            conflict_behavior: conflictBehavior,
        }),
    });
    if (!sessionResponse?.ok || !sessionResponse?.upload_url) {
        return {
            ok: false,
            code: sessionResponse?.code,
            message: sessionResponse?.message || "Failed to start the large file upload",
        };
    }

    const uploadUrl = String(sessionResponse.upload_url);
    const uploadedFileName = String(sessionResponse.file_name || file.name || "file");
    let offset = 0;
    let finalItem = null;
    onProgress?.({ loaded: 0, total: totalBytes, ratio: 0, lengthComputable: true });

    while (offset < totalBytes) {
        const start = offset;
        const endExclusive = Math.min(
            totalBytes,
            start + SHAREPOINT_UPLOAD_CHUNK_BYTES
        );
        let attempt = 0;

        while (true) {
            try {
                const chunkResponse = await uploadSharePointChunk(
                    uploadUrl,
                    file,
                    start,
                    endExclusive,
                    totalBytes,
                    onProgress
                );
                finalItem = chunkResponse.data?.id ? chunkResponse.data : finalItem;
                offset = chunkResponse.data?.id
                    ? totalBytes
                    : getUploadSessionOffset(chunkResponse.data, endExclusive);
                onProgress?.({
                    loaded: offset,
                    total: totalBytes,
                    ratio: totalBytes > 0 ? offset / totalBytes : 0,
                    lengthComputable: true,
                });
                break;
            } catch (error) {
                if (!isRetryableSharePointUploadError(error) || attempt >= SHAREPOINT_UPLOAD_MAX_RETRIES) {
                    throw error;
                }

                try {
                    const sessionState = await getSharePointUploadSessionState(uploadUrl);
                    const resumedOffset = getUploadSessionOffset(sessionState, start);
                    if (resumedOffset >= totalBytes) {
                        offset = totalBytes;
                        break;
                    }
                    if (resumedOffset !== start) {
                        offset = resumedOffset;
                        break;
                    }
                } catch {
                    // The retry below will either succeed or surface the original error.
                }

                await waitForSharePointUploadRetry(attempt);
                attempt += 1;
            }
        }
    }

    if (!finalItem?.id) {
        return {
            ok: false,
            message: "Upload completed but SharePoint did not return the uploaded file",
        };
    }

    return apiFetch(finalizePath, {
        method: "POST",
        body: JSON.stringify({
            path: path || "/",
            file_name: uploadedFileName,
            file_size: totalBytes,
            item_id: finalItem.id,
        }),
    });
}

/* ============================================================
   AUTH
============================================================ */

export async function login(email, password) {
    const rememberTwoFactorToken = getRememberedTwoFactorToken();
    return apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({
            email,
            password,
            rememberTwoFactorToken: rememberTwoFactorToken || null,
        }),
    });
}

export async function start2FASetup() {
    return apiFetch("/auth/2fa/setup", {
        method: "POST",
    });
}

export async function verify2FASetup(code) {
    return apiFetch("/auth/2fa/verify-setup", {
        method: "POST",
        body: JSON.stringify({ code }),
    });
}

export async function completeTwoFactorLogin(code, twoFactorToken) {
    return apiFetch("/auth/2fa/login", {
        method: "POST",
        body: JSON.stringify({ code, twoFactorToken }),
    });
}

export async function startPending2FASetup(setupToken, method = "totp") {
    return apiFetch("/auth/2fa/setup-pending", {
        method: "POST",
        body: JSON.stringify({ setupToken, method }),
    });
}

export async function verifyPending2FASetup(setupToken, code, method = "totp") {
    return apiFetch("/auth/2fa/verify-setup-pending", {
        method: "POST",
        body: JSON.stringify({ setupToken, code, method }),
    });
}

export async function resendTwoFactorLoginCode(twoFactorToken) {
    return apiFetch("/auth/2fa/login-resend", {
        method: "POST",
        body: JSON.stringify({ twoFactorToken }),
    });
}

export async function resendPending2FASetupCode(setupToken, method = "email") {
    return apiFetch("/auth/2fa/setup-pending-resend", {
        method: "POST",
        body: JSON.stringify({ setupToken, method }),
    });
}

export async function disable2FA() {
    return apiFetch("/auth/2fa/disable", {
        method: "POST",
    });
}

export async function disableUser2FA(userId) {
    return apiFetch(`/users/${userId}/disable-2fa`, {
        method: "POST",
    });
}

export async function getCurrentUser() {
    return apiFetch("/auth/me");
}

export async function updateCurrentUserEmail(email, currentPassword) {
    return apiFetch("/auth/me/email", {
        method: "PUT",
        body: JSON.stringify({
            email,
            current_password: currentPassword,
        }),
    });
}

export async function getPasskeys() {
    return apiFetch("/auth/passkeys");
}

export async function getPasskeyRegistrationOptions() {
    return apiFetch("/auth/passkeys/registration-options", { method: "POST" });
}

export async function verifyPasskeyRegistration(response, challenge, name) {
    return apiFetch("/auth/passkeys/registration-verification", {
        method: "POST",
        body: JSON.stringify({ response, challenge, name }),
    });
}

export async function getPendingPasskeyRegistrationOptions(setupToken) {
    return apiFetch("/auth/passkeys/registration-options-pending", {
        method: "POST",
        skipAuthFailureRedirect: true,
        body: JSON.stringify({ setupToken }),
    });
}

export async function verifyPendingPasskeyRegistration(
    setupToken,
    response,
    challenge,
    name
) {
    return apiFetch("/auth/passkeys/registration-verification-pending", {
        method: "POST",
        skipAuthFailureRedirect: true,
        body: JSON.stringify({ setupToken, response, challenge, name }),
    });
}

export async function revokePasskey(id) {
    return apiFetch(`/auth/passkeys/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function getPasskeyAuthenticationOptions(email) {
    return apiFetch("/auth/passkeys/authentication-options", {
        method: "POST",
        skipAuthFailureRedirect: true,
        body: JSON.stringify({ email: String(email || "").trim() || null }),
    });
}

export async function verifyPasskeyAuthentication(response, challenge) {
    return apiFetch("/auth/passkeys/authentication-verification", {
        method: "POST",
        skipAuthFailureRedirect: true,
        body: JSON.stringify({ response, challenge }),
    });
}

export async function getPushNotificationConfig() {
    return apiFetch("/push-notifications/config");
}

export async function savePushNotificationSubscription(subscription) {
    return apiFetch("/push-notifications/subscriptions", {
        method: "POST",
        body: JSON.stringify({ subscription }),
    });
}

export async function deletePushNotificationSubscription(endpoint) {
    return apiFetch("/push-notifications/subscriptions", {
        method: "DELETE",
        body: JSON.stringify({ endpoint }),
    });
}

export async function sendPushNotificationTest() {
    return apiFetch("/push-notifications/test", { method: "POST" });
}

export async function logout() {
    return apiFetch("/auth/logout", {
        method: "POST",
    });
}

export async function forgotPassword(email) {
    const res = await fetch(`${API_URL}/auth/password/forgot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
    });
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getPasswordTokenInfo(token) {
    const qs = new URLSearchParams({ token: String(token || "") });
    const res = await fetch(`${API_URL}/auth/password/token-info?${qs.toString()}`);
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function completePasswordSetup(token, password) {
    const res = await fetch(`${API_URL}/auth/password/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
    });
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

/* ============================================================
   USER MANAGEMENT
============================================================ */

export async function getUsers() {
    const data = await apiFetch("/users");
    if (!data.ok) throw new Error(data.message);
    return data.users;
}

export async function createUser(user) {
    const data = await apiFetch("/users", {
        method: "POST",
        body: JSON.stringify(user),
    });
    if (!data.ok) throw new Error(data.message);
    return data.user;
}

export async function updateUser(id, user) {
    const data = await apiFetch(`/users/${id}`, {
        method: "PUT",
        body: JSON.stringify(user),
    });
    if (!data.ok) throw new Error(data.message);
    return data.user;
}

export async function deleteUser(id) {
    const data = await apiFetch(`/users/${id}`, {
        method: "DELETE",
    });
    if (!data.ok) throw new Error(data.message);
    return true;
}

export async function resendUserPasswordSetup(id) {
    const data = await apiFetch(`/users/${id}/resend-password-setup`, {
        method: "POST",
    });
    if (!data.ok) throw new Error(data.message);
    return data;
}

export async function getUserPodcastPermissions(userId) {
    const data = await apiFetch(`/users/${userId}/podcast-permissions`);
    if (!data.ok) throw new Error(data.message);
    return data.permissions || [];
}

export async function updateUserPodcastPermissions(userId, permissions) {
    const data = await apiFetch(`/users/${userId}/podcast-permissions`, {
        method: "PUT",
        body: JSON.stringify({ permissions }),
    });
    if (!data.ok) throw new Error(data.message);
    return data.permissions || [];
}

/* ============================================================
   XERO + BILLING
============================================================ */

export async function getXeroStatus() {
    const data = await apiFetch("/xero/status");
    if (!data.ok) throw new Error(data.message);
    return data;
}

export async function startXeroConnection() {
    const data = await apiFetch("/xero/oauth/start", { method: "POST" });
    if (!data.ok) throw new Error(data.message);
    return data.authorization_url;
}

export async function disconnectXero(connectionId) {
    const data = await apiFetch(
        "/xero/connections/" + encodeURIComponent(connectionId) + "/disconnect",
        { method: "POST" }
    );
    if (!data.ok) throw new Error(data.message);
    return true;
}

export async function refreshXeroBilling() {
    const data = await apiFetch("/xero/billing/refresh", { method: "POST" });
    if (!data.ok) {
        const warning = Array.isArray(data.warnings) ? data.warnings[0] : "";
        throw new Error(data.message || warning || "Could not refresh Xero billing data.");
    }
    return data;
}

export async function getXeroContacts({ connectionId, search = "" } = {}) {
    const qs = new URLSearchParams();
    if (connectionId) qs.set("connection_id", String(connectionId));
    if (search) qs.set("search", search);
    const data = await apiFetch("/xero/contacts?" + qs.toString());
    if (!data.ok) throw new Error(data.message);
    return data.contacts || [];
}

export async function getClientXeroMapping(clientId) {
    const data = await apiFetch("/xero/clients/" + clientId + "/contact");
    if (!data.ok) throw new Error(data.message);
    return data.mapping || null;
}

export async function setClientXeroMapping(clientId, connectionId, contactId, billingEmail) {
    const body = {
        connection_id: connectionId,
        contact_id: contactId,
    };
    if (billingEmail !== undefined) {
        body.billing_email = billingEmail || null;
    }
    const data = await apiFetch("/xero/clients/" + clientId + "/contact", {
        method: "PUT",
        body: JSON.stringify(body),
    });
    if (!data.ok) throw new Error(data.message);
    return data.mapping || null;
}

export async function clearClientXeroMapping(clientId) {
    const data = await apiFetch("/xero/clients/" + clientId + "/contact", {
        method: "DELETE",
    });
    if (!data.ok) throw new Error(data.message);
    return true;
}

export async function getBillingInvoices({ page = 1, pageSize = 10, clientId = "", search = "", status = "" } = {}) {
    const qs = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
    });
    if (clientId) qs.set("client_id", String(clientId));
    if (search) qs.set("search", String(search));
    if (status) qs.set("status", String(status));
    const data = await apiFetch("/billing/invoices?" + qs.toString());
    if (!data.ok) throw new Error(data.message);
    return data;
}

export async function getBillingInvoice(invoiceId) {
    const data = await apiFetch("/billing/invoices/" + encodeURIComponent(invoiceId));
    if (!data.ok) throw new Error(data.message);
    return data.invoice || null;
}

export async function resendBillingInvoice(invoiceId) {
    const data = await apiFetch(
        "/billing/invoices/" + encodeURIComponent(invoiceId) + "/resend",
        {
            method: "POST",
            body: JSON.stringify({}),
        }
    );
    if (!data.ok) throw new Error(data.message);
    return data;
}

export async function downloadBillingInvoicePdf(invoiceId) {
    const token = getToken();
    const response = await fetch(
        API_URL + "/billing/invoices/" + encodeURIComponent(invoiceId) + "/pdf",
        {
            headers: token ? { Authorization: "Bearer " + token } : {},
        }
    );
    if (response.status === 401) {
        handleAuthFailure();
        throw new Error("Invalid or expired token");
    }
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Failed to download invoice PDF");
    }
    return {
        blob: await response.blob(),
        disposition: response.headers.get("Content-Disposition") || "",
    };
}

/* ============================================================
   RECORDING SESSIONS
============================================================ */

export async function getRecordingSessions() {
    const data = await apiFetch("/recording-sessions");
    if (!data.ok) throw new Error(data.message);
    return data.sessions || [];
}

export async function createRecordingSession(payload) {
    const data = await apiFetch("/recording-sessions", {
        method: "POST",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data.session;
}

export async function getRecordingCalendarSettings() {
    const data = await apiFetch("/recording-sessions/settings/calendar");
    if (!data.ok) throw new Error(data.message);
    return data.settings;
}

export async function updateRecordingCalendarSettings(payload) {
    const data = await apiFetch("/recording-sessions/settings/calendar", {
        method: "PUT",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data.settings;
}

export async function getRecordingCalendars(userId) {
    const qs = new URLSearchParams({ user_id: String(userId || "").trim() });
    const data = await apiFetch(`/recording-sessions/settings/calendar/calendars?${qs.toString()}`);
    if (!data.ok) throw new Error(data.message);
    return data.calendars || [];
}

export async function getRecordingSession(id) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}`);
    if (!data.ok) throw new Error(data.message);
    return data.session;
}

export async function ensureRecordingSessionPortalHostParticipant(id) {
    const data = await apiFetch(
        `/recording-sessions/${encodeURIComponent(id)}/portal-host-participant`,
        {
            method: "POST",
            body: JSON.stringify({}),
        }
    );
    if (!data.ok) {
        throw new Error(data.message || "Failed to prepare Recording Studio access");
    }
    return data.participant;
}

export async function getRecordingSessionDiagnostics(id) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}/diagnostics`);
    if (!data.ok) throw new Error(data.message || "Failed to load recording diagnostics");
    return data.diagnostics;
}

export async function forceRemoveRecordingParticipant(
    sessionId,
    participantId,
    payload = {},
    { guest = false } = {}
) {
    const path = `/recording-sessions/${encodeURIComponent(sessionId)}/participants/${encodeURIComponent(participantId)}/force-remove`;
    const options = { method: "POST", body: JSON.stringify(payload) };
    const data = guest
        ? await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { "Content-Type": "application/json" },
        }).then((res) => res.json().catch(() => ({})))
        : await apiFetch(path, options);
    if (!data.ok) throw new Error(data.message || "Failed to remove participant");
    return data;
}

export async function submitRecordingParticipantDiagnostics(participantId, payload, diagnosticsToken = "") {
    const data = await apiFetch(`/recording-sessions/participants/${encodeURIComponent(participantId)}/diagnostics`, {
        method: "POST",
        headers: diagnosticsToken
            ? { "X-Recording-Participant-Diagnostics-Token": diagnosticsToken }
            : undefined,
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message || "Failed to save recording diagnostics");
    return data;
}

export async function downloadRecordingSessionDiagnostics(id) {
    const token = getToken();
    const res = await fetch(`${API_URL}/recording-sessions/${encodeURIComponent(id)}/diagnostics/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed to export recording diagnostics");
    }
    const blob = await res.blob();
    const disposition = String(res.headers.get("Content-Disposition") || "");
    const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || `recording-session-${id}-diagnostics.json`;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
}

export async function updateRecordingSession(id, payload) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data.session;
}

export async function deleteRecordingSession(id) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}`, {
        method: "DELETE",
    });
    if (!data.ok) throw new Error(data.message);
    return data;
}

export async function cancelRecordingSession(id) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}/cancel`, {
        method: "POST",
    });
    if (!data.ok) throw new Error(data.message || "Failed to cancel recording session");
    return data.session;
}

export async function reopenRecordingSession(id) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}/reopen`, {
        method: "POST",
    });
    if (!data.ok) throw new Error(data.message);
    return data.session;
}

export async function restoreRecordingSession(id) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}/restore`, {
        method: "POST",
    });
    if (!data.ok) throw new Error(data.message || "Failed to restore recording session");
    return data.session;
}

export async function recoverRecordingSessionUploads(id, payload = {}) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}/recover-uploads`, {
        method: "POST",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message || "Failed to recover recording uploads");
    return data;
}

export async function queueRecordingSessionTranscription(id) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}/transcription`, {
        method: "POST",
        body: JSON.stringify({}),
    });
    if (!data.ok) throw new Error(data.message || "Failed to queue recording transcription");
    return data;
}

export async function cleanupRecordingTranscript(fileId) {
    const data = await apiFetch(
        `/recording-sessions/files/${encodeURIComponent(fileId)}/cleanup-transcript`,
        {
            method: "POST",
            body: JSON.stringify({}),
        }
    );
    if (!data.ok) throw new Error(data.message || "Failed to clean up transcript");
    return data;
}

export async function getNamedRecordingTranscriptPreview(id, takeNumber) {
    const data = await apiFetch(
        `/recording-sessions/${encodeURIComponent(id)}/transcription/${encodeURIComponent(takeNumber)}/named-preview`
    );
    if (!data.ok) throw new Error(data.message || "Failed to preview named transcript");
    return data;
}

export async function regenerateNamedRecordingTranscript(id, takeNumber) {
    const data = await apiFetch(
        `/recording-sessions/${encodeURIComponent(id)}/transcription/${encodeURIComponent(takeNumber)}/regenerate-named`,
        {
            method: "POST",
            body: JSON.stringify({}),
        }
    );
    if (!data.ok) throw new Error(data.message || "Failed to regenerate named transcript");
    return data;
}

export async function createRecordingInvite(id, payload = {}) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}/invites`, {
        method: "POST",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data.invite;
}

export async function getRecordingInviteCandidates(id) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}/invite-candidates`);
    if (!data.ok) throw new Error(data.message || "Failed to load invite candidates");
    return data.candidates || [];
}

export async function deleteRecordingInvite(id, inviteId) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}/invites/${encodeURIComponent(inviteId)}`, {
        method: "DELETE",
    });
    if (!data.ok) throw new Error(data.message || "Failed to cancel invite");
    return data.session;
}

export async function linkRecordingEpisode(id, episodeId) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}/link-episode`, {
        method: "POST",
        body: JSON.stringify({ episode_id: episodeId || null }),
    });
    if (!data.ok) throw new Error(data.message);
    return data.session;
}

export async function getRecordingInvite(inviteToken) {
    const res = await fetch(
        `${API_URL}/recording-sessions/join/${encodeURIComponent(inviteToken)}`,
        { headers: getOptionalAuthHeaders() }
    );
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to load invite");
    return data.invite;
}

export async function joinRecordingInvite(inviteToken, payload) {
    const res = await fetch(
        `${API_URL}/recording-sessions/join/${encodeURIComponent(inviteToken)}`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...getOptionalAuthHeaders(),
            },
            body: JSON.stringify(payload),
        }
    );
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to join recording session");
    return data;
}

export async function getGuestRecordingParticipant(inviteToken, participantId) {
    const res = await fetch(
        `${API_URL}/recording-sessions/join/${encodeURIComponent(inviteToken)}/participant/${encodeURIComponent(participantId)}`
    );
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to load participant");
    return data;
}

export async function getRecordingLiveKitToken(id, payload = {}) {
    const data = await apiFetch(
        `/recording-sessions/${encodeURIComponent(id)}/livekit-token`,
        {
            method: "POST",
            body: JSON.stringify(payload),
        }
    );
    if (!data.ok) throw new Error(data.message);
    return data;
}

export async function getGuestRecordingLiveKitToken(id, payload = {}) {
    const res = await fetch(
        `${API_URL}/recording-sessions/${encodeURIComponent(id)}/livekit-token`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }
    );
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to create LiveKit token");
    return data;
}

export async function startRecordingSession(id, payload = {}, { guest = false } = {}) {
    const path = `/recording-sessions/${encodeURIComponent(id)}/start-recording`;
    const options = {
        method: "POST",
        body: JSON.stringify(payload),
    };
    const data = guest
        ? await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { "Content-Type": "application/json" },
        }).then((res) => res.json().catch(() => ({})))
        : await apiFetch(path, options);
    if (!data.ok) throw new Error(data.message);
    return data.session;
}

export async function stopRecordingSession(id, payload = {}, { guest = false } = {}) {
    const path = `/recording-sessions/${encodeURIComponent(id)}/stop-recording`;
    const options = {
        method: "POST",
        body: JSON.stringify(payload),
    };
    const data = guest
        ? await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { "Content-Type": "application/json" },
        }).then((res) => res.json().catch(() => ({})))
        : await apiFetch(path, options);
    if (!data.ok) throw new Error(data.message);
    return data.session;
}

export async function terminateRecordingSession(id) {
    const data = await apiFetch(`/recording-sessions/${encodeURIComponent(id)}/terminate`, {
        method: "POST",
    });
    if (!data.ok) throw new Error(data.message || "Failed to terminate recording session");
    return data.session;
}

export async function admitRecordingParticipant(id, participantId, payload = {}, { guest = false } = {}) {
    const path = `/recording-sessions/${encodeURIComponent(id)}/lobby/admit`;
    const options = {
        method: "POST",
        body: JSON.stringify({ ...payload, target_participant_id: participantId }),
    };
    const data = guest
        ? await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { "Content-Type": "application/json" },
        }).then((res) => res.json().catch(() => ({})))
        : await apiFetch(path, options);
    if (!data.ok) throw new Error(data.message);
    return data.session;
}

export async function rejectRecordingParticipant(id, participantId, payload = {}, { guest = false } = {}) {
    const path = `/recording-sessions/${encodeURIComponent(id)}/lobby/reject`;
    const options = {
        method: "POST",
        body: JSON.stringify({ ...payload, target_participant_id: participantId }),
    };
    const data = guest
        ? await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { "Content-Type": "application/json" },
        }).then((res) => res.json().catch(() => ({})))
        : await apiFetch(path, options);
    if (!data.ok) throw new Error(data.message);
    return data.session;
}

export async function createRecordingMarker(id, payload, { guest = false } = {}) {
    const path = `/recording-sessions/${encodeURIComponent(id)}/markers`;
    if (!guest) {
        const data = await apiFetch(path, {
            method: "POST",
            body: JSON.stringify(payload),
        });
        if (!data.ok) throw new Error(data.message);
        return data.marker;
    }
    const res = await fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to create marker");
    return data.marker;
}

export async function updateRecordingParticipantStatus(participantId, payload, { guest = false } = {}) {
    const path = `/recording-sessions/participants/${encodeURIComponent(participantId)}/status`;
    if (!guest) {
        const data = await apiFetch(path, {
            method: "POST",
            body: JSON.stringify(payload),
        });
        if (!data.ok) throw new Error(data.message);
        return data;
    }
    const res = await fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to update participant status");
    return data;
}

export async function getRecordingParticipantNotes(participantId, { guestToken = "" } = {}) {
    const path = `/recording-sessions/participants/${encodeURIComponent(participantId)}/notes`;
    if (!guestToken) {
        const data = await apiFetch(path);
        if (!data.ok) throw new Error(data.message || "Failed to load participant notes");
        return data;
    }
    const res = await fetch(`${API_URL}${path}`, {
        headers: {
            "X-Recording-Participant-Notes-Token": guestToken,
        },
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to load participant notes");
    return data;
}

export async function updateRecordingParticipantNotes(
    participantId,
    notes,
    { guestToken = "", keepalive = false } = {}
) {
    const path = `/recording-sessions/participants/${encodeURIComponent(participantId)}/notes`;
    const options = {
        method: "PUT",
        body: JSON.stringify({ notes }),
        keepalive,
    };
    if (!guestToken) {
        const data = await apiFetch(path, options);
        if (!data.ok) throw new Error(data.message || "Failed to save participant notes");
        return data;
    }
    const res = await fetch(`${API_URL}${path}`, {
        ...options,
        headers: {
            "Content-Type": "application/json",
            "X-Recording-Participant-Notes-Token": guestToken,
        },
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to save participant notes");
    return data;
}

function getRecordingChatHeaders(guestToken = "", json = false) {
    return {
        ...(json ? { "Content-Type": "application/json" } : {}),
        ...(guestToken ? { "X-Recording-Participant-Chat-Token": guestToken } : {}),
    };
}

async function recordingChatFetch(path, options = {}, guestToken = "", experience = "live") {
    const normalizedExperience = String(experience || "").trim().toLowerCase() === "beta" ? "beta" : "live";
    const experienceHeaders = {
        "X-Recording-Studio-Experience": normalizedExperience,
        ...(options.headers || {}),
    };
    if (!guestToken) return apiFetch(path, { ...options, headers: experienceHeaders });
    const res = await fetch(`${API_URL}${path}`, {
        ...options,
        headers: {
            ...getRecordingChatHeaders(guestToken, Boolean(options.body)),
            ...experienceHeaders,
        },
    });
    return res.json().catch(() => ({}));
}

export async function getRecordingChat(participantId, { guestToken = "", experience = "live" } = {}) {
    const path = `/recording-sessions/participants/${encodeURIComponent(participantId)}/chat`;
    const data = await recordingChatFetch(path, {}, guestToken, experience);
    if (!data.ok) throw new Error(data.message || "Failed to load Studio chat");
    return data;
}

export async function getRecordingChatMessages(
    participantId,
    conversation,
    { beforeId = null, guestToken = "", experience = "live" } = {}
) {
    const params = new URLSearchParams({ conversation });
    if (beforeId) params.set("before_id", String(beforeId));
    const path = `/recording-sessions/participants/${encodeURIComponent(participantId)}/chat/messages?${params}`;
    const data = await recordingChatFetch(path, {}, guestToken, experience);
    if (!data.ok) throw new Error(data.message || "Failed to load Studio chat messages");
    return data;
}

export async function sendRecordingChatMessage(
    participantId,
    { conversation, message, clientMessageId },
    { guestToken = "", experience = "live" } = {}
) {
    const path = `/recording-sessions/participants/${encodeURIComponent(participantId)}/chat/messages`;
    const data = await recordingChatFetch(path, {
        method: "POST",
        body: JSON.stringify({
            conversation,
            message,
            client_message_id: clientMessageId,
        }),
    }, guestToken, experience);
    if (!data.ok) throw new Error(data.message || "Failed to send Studio chat message");
    return data;
}

export async function markRecordingChatRead(
    participantId,
    { conversation, lastReadMessageId },
    { guestToken = "", experience = "live" } = {}
) {
    const path = `/recording-sessions/participants/${encodeURIComponent(participantId)}/chat/read`;
    const data = await recordingChatFetch(path, {
        method: "PUT",
        body: JSON.stringify({
            conversation,
            last_read_message_id: lastReadMessageId,
        }),
    }, guestToken, experience);
    if (!data.ok) throw new Error(data.message || "Failed to update Studio chat");
    return data;
}

export async function getRecordingSharedNotes(participantId, { guestToken = "" } = {}) {
    const path = `/recording-sessions/participants/${encodeURIComponent(participantId)}/shared-notes`;
    if (!guestToken) {
        const data = await apiFetch(path);
        if (!data.ok) throw new Error(data.message || "Failed to load shared notes");
        return data;
    }
    const res = await fetch(`${API_URL}${path}`, {
        headers: { "X-Recording-Participant-Notes-Token": guestToken },
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to load shared notes");
    return data;
}

export async function updateRecordingSharedNotes(
    participantId,
    update,
    { guestToken = "", keepalive = false } = {}
) {
    const path = `/recording-sessions/participants/${encodeURIComponent(participantId)}/shared-notes/updates`;
    const options = {
        method: "POST",
        body: JSON.stringify({ update }),
        keepalive,
    };
    if (!guestToken) {
        const data = await apiFetch(path, options);
        if (!data.ok) throw new Error(data.message || "Failed to save shared notes");
        return data;
    }
    const res = await fetch(`${API_URL}${path}`, {
        ...options,
        headers: {
            "Content-Type": "application/json",
            "X-Recording-Participant-Notes-Token": guestToken,
        },
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to save shared notes");
    return data;
}

export async function updateRecordingSharedNotesAccess(
    sessionId,
    sharedNotesGuestAccess,
    { participantId = null, inviteToken = "", guest = false } = {}
) {
    const path = `/recording-sessions/${encodeURIComponent(sessionId)}/shared-notes-access`;
    const body = JSON.stringify({
        shared_notes_guest_access: sharedNotesGuestAccess,
        participant_id: participantId,
        invite_token: inviteToken,
    });
    if (!guest) {
        const data = await apiFetch(path, { method: "PATCH", body });
        if (!data.ok) throw new Error(data.message || "Failed to update shared notes access");
        return data;
    }
    const res = await fetch(`${API_URL}${path}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body,
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to update shared notes access");
    return data;
}

export async function updateRecordingParticipantCallVolume(participantId, payload, { guest = false } = {}) {
    const path = `/recording-sessions/participants/${encodeURIComponent(participantId)}/call-volume`;
    if (!guest) {
        const data = await apiFetch(path, {
            method: "POST",
            body: JSON.stringify(payload),
        });
        if (!data.ok) throw new Error(data.message);
        return data;
    }
    const res = await fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to update call volume");
    return data;
}

export async function muteRecordingParticipant(participantId) {
    const data = await apiFetch(`/recording-sessions/participants/${encodeURIComponent(participantId)}/mute`, {
        method: "POST",
    });
    if (!data.ok) throw new Error(data.message);
    return data;
}

export async function unmuteRecordingParticipant(participantId) {
    const data = await apiFetch(`/recording-sessions/participants/${encodeURIComponent(participantId)}/unmute`, {
        method: "POST",
    });
    if (!data.ok) throw new Error(data.message);
    return data;
}

export async function updateRecordingParticipantRecording(participantId, payload, { guest = false } = {}) {
    const path = `/recording-sessions/participants/${encodeURIComponent(participantId)}/recording`;
    if (!guest) {
        const data = await apiFetch(path, {
            method: "POST",
            body: JSON.stringify(payload),
        });
        if (!data.ok) throw new Error(data.message);
        return data;
    }
    const res = await fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to update recording choice");
    return data;
}

export async function updateRecordingParticipantGalleryVisibility(participantId, hideFromGallery) {
    const data = await apiFetch(
        `/recording-sessions/participants/${encodeURIComponent(participantId)}/gallery-visibility`,
        {
            method: "POST",
            body: JSON.stringify({ hide_from_gallery: Boolean(hideFromGallery) }),
        }
    );
    if (!data.ok) throw new Error(data.message || "Failed to update gallery visibility");
    return data;
}

export async function createRecordingFile(payload, { guest = false } = {}) {
    const path = "/recording-sessions/files";
    if (!guest) {
        const data = await apiFetch(path, {
            method: "POST",
            body: JSON.stringify(payload),
        });
        if (!data.ok) {
            const err = new Error(data.message);
            err.code = data.code || null;
            throw err;
        }
        return data.file;
    }
    const res = await fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) {
        const err = new Error(data.message || "Failed to create recording file");
        err.code = data.code || null;
        throw err;
    }
    return data.file;
}

export async function getRecordingUploadBlockUrl(fileId, blockIndex) {
    const data = await apiFetch(
        `/recording-sessions/files/${encodeURIComponent(fileId)}/upload-block-url`,
        {
            method: "POST",
            body: JSON.stringify({ block_index: blockIndex }),
        }
    );
    if (!data.ok) throw new Error(data.message);
    return data;
}

export async function getRecordingUploadBlockUrls(fileId, blockIndexes) {
    const indexes = [...new Set((Array.isArray(blockIndexes) ? blockIndexes : []).map(Number))];
    const data = await apiFetch(
        `/recording-sessions/files/${encodeURIComponent(fileId)}/upload-block-url`,
        {
            method: "POST",
            body: JSON.stringify({ block_indexes: indexes }),
        }
    );
    if (!data.ok) throw new Error(data.message);
    return Array.isArray(data.blocks) ? data.blocks : [];
}

export async function getGuestRecordingUploadBlockUrl(fileId, blockIndex, inviteToken = "") {
    const res = await fetch(
        `${API_URL}/recording-sessions/files/${encodeURIComponent(fileId)}/upload-block-url`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ block_index: blockIndex, invite_token: inviteToken }),
        }
    );
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to create upload URL");
    return data;
}

export async function getGuestRecordingUploadBlockUrls(fileId, blockIndexes, inviteToken = "") {
    const indexes = [...new Set((Array.isArray(blockIndexes) ? blockIndexes : []).map(Number))];
    const res = await fetch(
        `${API_URL}/recording-sessions/files/${encodeURIComponent(fileId)}/upload-block-url`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ block_indexes: indexes, invite_token: inviteToken }),
        }
    );
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to create upload URLs");
    return Array.isArray(data.blocks) ? data.blocks : [];
}

export async function getRecordingThumbnailUploadUrl(fileId, { guest = false, inviteToken = "" } = {}) {
    const path = `/recording-sessions/files/${encodeURIComponent(fileId)}/thumbnail-upload-url`;
    if (!guest) {
        const data = await apiFetch(path, {
            method: "POST",
            body: JSON.stringify({}),
        });
        if (!data.ok) throw new Error(data.message);
        return data;
    }
    const res = await fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invite_token: inviteToken }),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to create thumbnail upload URL");
    return data;
}

export async function saveRecordingThumbnail(fileId, blobPath, { guest = false, inviteToken = "" } = {}) {
    const path = `/recording-sessions/files/${encodeURIComponent(fileId)}/thumbnail`;
    const body = { blob_path: blobPath };
    if (!guest) {
        const data = await apiFetch(path, {
            method: "POST",
            body: JSON.stringify(body),
        });
        if (!data.ok) throw new Error(data.message);
        return data.file;
    }
    const res = await fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, invite_token: inviteToken }),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to save thumbnail");
    return data.file;
}

export async function commitRecordingFile(fileId, blocks, { guest = false, inviteToken = "", durationSeconds = null } = {}) {
    const path = `/recording-sessions/files/${encodeURIComponent(fileId)}/commit`;
    const body = {
        blocks,
        ...(Number.isFinite(Number(durationSeconds)) ? { duration_seconds: Math.max(0, Math.round(Number(durationSeconds))) } : {}),
    };
    if (!guest) {
        const data = await apiFetch(path, {
            method: "POST",
            body: JSON.stringify(body),
        });
        if (!data.ok) throw new Error(data.message);
        return data.file;
    }
    const res = await fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, invite_token: inviteToken }),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to commit upload");
    return data.file;
}

export async function discardEmptyRecordingFile(
    fileId,
    participantId,
    { guest = false, inviteToken = "" } = {}
) {
    const path = `/recording-sessions/files/${encodeURIComponent(fileId)}/discard-empty`;
    const body = {
        participant_id: participantId,
        ...(guest ? { invite_token: inviteToken } : {}),
    };
    if (!guest) {
        const data = await apiFetch(path, {
            method: "POST",
            body: JSON.stringify(body),
        });
        if (!data.ok) throw new Error(data.message || "Failed to discard empty recording");
        return data;
    }
    const res = await fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.message || "Failed to discard empty recording");
    return data;
}

export async function getRecordingFileDownloadUrl(fileId) {
    const data = await apiFetch(
        `/recording-sessions/files/${encodeURIComponent(fileId)}/download-url`
    );
    if (!data.ok) throw new Error(data.message);
    return data.download_url;
}

export async function retryRecordingFileConversion(fileId) {
    const data = await apiFetch(
        `/recording-sessions/files/${encodeURIComponent(fileId)}/retry-conversion`,
        { method: "POST" }
    );
    if (!data.ok) throw new Error(data.message);
    return data.session;
}

export async function convertRecordingSessionToMp4(sessionId) {
    const data = await apiFetch(
        `/recording-sessions/${encodeURIComponent(sessionId)}/convert-to-mp4`,
        { method: "POST" }
    );
    if (!data.ok) throw new Error(data.message);
    return data.session;
}

export async function downloadRecordingFile(fileId) {
    const token = getToken();
    const res = await fetch(
        `${API_URL}/recording-sessions/files/${encodeURIComponent(fileId)}/download`,
        {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
    );
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed to download recording file");
    }
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition") || "";
    return { blob, disposition };
}

export function buildRecordingFileStreamProxyUrl(fileId) {
    const token = getToken();
    const qs = new URLSearchParams({
        access_token: String(token || ""),
    });
    return `${API_URL}/recording-sessions/files/${encodeURIComponent(fileId)}/stream?${qs.toString()}`;
}

export function buildRecordingFileDownloadProxyUrl(fileId) {
    const token = getToken();
    const qs = new URLSearchParams({
        access_token: String(token || ""),
    });
    return `${API_URL}/recording-sessions/files/${encodeURIComponent(fileId)}/download?${qs.toString()}`;
}

export function buildRecordingFileThumbnailProxyUrl(fileId) {
    const token = getToken();
    const qs = new URLSearchParams({
        access_token: String(token || ""),
    });
    return `${API_URL}/recording-sessions/files/${encodeURIComponent(fileId)}/thumbnail?${qs.toString()}`;
}

/* ============================================================
   CLIENTS
============================================================ */

export async function getClients({ status = "active" } = {}) {
    const qs = new URLSearchParams({ status });
    const data = await apiFetch(`/clients?${qs.toString()}`);
    if (!data.ok) throw new Error(data.message);
    return data.clients;
}

export async function createClient(client) {
    const data = await apiFetch("/clients", {
        method: "POST",
        body: JSON.stringify(client),
    });
    if (!data.ok) throw new Error(data.message);
    return data.client;
}

export async function updateClient(id, client) {
    const data = await apiFetch(`/clients/${id}`, {
        method: "PUT",
        body: JSON.stringify(client),
    });
    if (!data.ok) throw new Error(data.message);
    return data.client;
}

export async function deleteClient(id) {
    const data = await apiFetch(`/clients/${id}`, { method: "DELETE" });
    if (!data.ok) throw new Error(data.message);
    return true;
}

/* ============================================================
   LEADS
============================================================ */

export async function getLeads(filters = {}) {
    const qs = new URLSearchParams();

    const search = String(filters.search || "").trim();
    const status = String(filters.status || "").trim();
    const podcastType = String(filters.podcast_type || "").trim();
    const frequency = String(filters.frequency || "").trim();
    const alreadyHasPodcast = String(filters.already_has_podcast || "").trim();

    if (search) qs.set("search", search);
    if (status) qs.set("status", status);
    if (podcastType) qs.set("podcast_type", podcastType);
    if (frequency) qs.set("frequency", frequency);
    if (alreadyHasPodcast) qs.set("already_has_podcast", alreadyHasPodcast);

    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const data = await apiFetch(`/leads${suffix}`);
    if (!data.ok) throw new Error(data.message);
    return data.leads || [];
}

export async function getLeadMetrics(days = 30) {
    const qs = new URLSearchParams({ days: String(days || 30) });
    const data = await apiFetch(`/leads/metrics?${qs.toString()}`);
    if (!data.ok) throw new Error(data.message);
    return data.metrics;
}

export async function createLead(payload) {
    const data = await apiFetch("/leads", {
        method: "POST",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data.lead;
}

export async function updateLead(id, payload) {
    const data = await apiFetch(`/leads/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data.lead;
}

export async function deleteLead(id) {
    const data = await apiFetch(`/leads/${id}`, { method: "DELETE" });
    if (!data.ok) throw new Error(data.message);
    return true;
}

export async function getLeadDiscoverySettings() {
    const data = await apiFetch("/leads/discovery-settings");
    if (!data.ok) throw new Error(data.message);
    return data.settings;
}

export async function updateLeadDiscoverySettings(payload) {
    const data = await apiFetch("/leads/discovery-settings", {
        method: "PUT",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data.settings;
}

export async function getOutlookCalendarsForMailbox(userId) {
    const qs = new URLSearchParams({ user_id: String(userId || "").trim() });
    const data = await apiFetch(`/leads/discovery-settings/calendars?${qs.toString()}`);
    if (!data.ok) throw new Error(data.message);
    return data.calendars || [];
}

export async function resendLeadDiscoveryInvite(leadId) {
    const data = await apiFetch(`/leads/${leadId}/discovery-call/resend`, {
        method: "POST",
    });
    if (!data.ok) throw new Error(data.message);
    return true;
}

export async function getDiscoveryCallPage(token, date = "", debug = false) {
    const qs = new URLSearchParams();
    if (date) qs.set("date", date);
    if (debug) qs.set("debug", "1");
    const suffix = qs.toString() ? `?${qs.toString()}` : "";

    const res = await fetch(
        `${API_URL}/discovery-call/${encodeURIComponent(token)}${suffix}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getDiscoveryCallMonthAvailability(token, month = "") {
    const qs = new URLSearchParams();
    if (month) qs.set("month", month);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await fetch(
        `${API_URL}/discovery-call/${encodeURIComponent(token)}/availability-month${suffix}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function bookDiscoveryCall(token, payload) {
    const res = await fetch(
        `${API_URL}/discovery-call/${encodeURIComponent(token)}/book`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function rescheduleDiscoveryCall(token, payload) {
    const res = await fetch(
        `${API_URL}/discovery-call/${encodeURIComponent(token)}/reschedule`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function cancelDiscoveryCall(token) {
    const res = await fetch(
        `${API_URL}/discovery-call/${encodeURIComponent(token)}/cancel`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getPublicDiscoveryAvailability(date = "") {
    const qs = new URLSearchParams();
    if (date) qs.set("date", date);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await fetch(`${API_URL}/discovery-call/public/availability${suffix}`);
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getPublicDiscoveryMonthAvailability(month = "") {
    const qs = new URLSearchParams();
    if (month) qs.set("month", month);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await fetch(
        `${API_URL}/discovery-call/public/availability-month${suffix}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function createPublicDiscoveryBooking(payload) {
    const res = await fetch(`${API_URL}/discovery-call/public/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getMeetingRequestSettings() {
    const data = await apiFetch("/meeting-requests/settings");
    if (!data.ok) throw new Error(data.message);
    return data.settings;
}

export async function updateMeetingRequestSettings(payload) {
    const data = await apiFetch("/meeting-requests/settings", {
        method: "PUT",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data.settings;
}

export async function getMeetingRequestCalendars(userId) {
    const qs = new URLSearchParams({ user_id: String(userId || "").trim() });
    const data = await apiFetch(`/meeting-requests/settings/calendars?${qs.toString()}`);
    if (!data.ok) throw new Error(data.message);
    return data.calendars || [];
}

export async function getMeetingRequests() {
    const data = await apiFetch("/meeting-requests");
    if (!data.ok) throw new Error(data.message);
    return data.requests || [];
}

export async function createMeetingRequest(payload) {
    const data = await apiFetch("/meeting-requests", {
        method: "POST",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message);
    return data;
}

export async function getMeetingRequestPage(token, date = "") {
    const qs = new URLSearchParams();
    if (date) qs.set("date", date);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await fetch(
        `${API_URL}/meeting-requests/public/${encodeURIComponent(token)}${suffix}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getMeetingRequestMonthAvailability(token, month = "") {
    const qs = new URLSearchParams();
    if (month) qs.set("month", month);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await fetch(
        `${API_URL}/meeting-requests/public/${encodeURIComponent(token)}/availability-month${suffix}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function bookMeetingRequest(token, payload) {
    const res = await fetch(
        `${API_URL}/meeting-requests/public/${encodeURIComponent(token)}/book`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function rescheduleMeetingRequest(token, payload) {
    const res = await fetch(
        `${API_URL}/meeting-requests/public/${encodeURIComponent(token)}/reschedule`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function cancelMeetingRequest(token) {
    const res = await fetch(
        `${API_URL}/meeting-requests/public/${encodeURIComponent(token)}/cancel`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

/* ============================================================
   PODCASTS
============================================================ */

export async function getPodcasts({ status = "active" } = {}) {
    const qs = new URLSearchParams({ status });
    return apiFetch(`/podcasts?${qs.toString()}`);
}

export async function createPodcast(payload) {
    return apiFetch("/podcasts", {
        method: "POST",
        body: JSON.stringify(payload),
    });
}

export async function updatePodcast(id, payload) {
    return apiFetch(`/podcasts/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
    });
}

export async function deletePodcast(id) {
    return apiFetch(`/podcasts/${id}`, {
        method: "DELETE",
    });
}

export async function runPodcastReleaseScheduleDigest(sendWebhook = false) {
    return apiFetch("/podcasts/release-schedule-digest/run-now", {
        method: "POST",
        body: JSON.stringify({ send_webhook: Boolean(sendWebhook) }),
    });
}

/* ============================================================
   CAPTIVATE ANALYTICS (ADMIN ONLY)
============================================================ */

export async function getCaptivateAnalyticsPodcasts() {
    return apiFetch("/captivate-analytics/podcasts");
}

export async function getCaptivateAnalytics(podcastId) {
    return apiFetch(`/captivate-analytics/podcasts/${encodeURIComponent(podcastId)}`);
}

export async function startCaptivateAnalyticsSync(payload = {}) {
    return apiFetch("/captivate-analytics/sync", {
        method: "POST",
        body: JSON.stringify(payload),
    });
}

export async function getCaptivateAnalyticsRun(runId) {
    return apiFetch(`/captivate-analytics/runs/${encodeURIComponent(runId)}`);
}

/* ============================================================
   EPISODES
============================================================ */

export async function getEpisodes({
    page,
    page_size,
    limit,
    status,
    podcast_ids,
    search,
} = {}) {
    const qs = new URLSearchParams();
    if (page !== undefined && page !== null) qs.set("page", String(page));
    if (page_size !== undefined && page_size !== null) {
        qs.set("page_size", String(page_size));
    } else if (limit !== undefined && limit !== null) {
        qs.set("limit", String(limit));
    }
    if (status) qs.set("status", String(status));
    if (podcast_ids) {
        const value = Array.isArray(podcast_ids) ? podcast_ids.join(",") : podcast_ids;
        if (String(value).trim()) qs.set("podcast_ids", String(value));
    }
    if (String(search || "").trim()) qs.set("search", String(search).trim());
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch(`/episodes${suffix}`);
}

export async function getDashboardSummary() {
    return apiFetch("/dashboard/summary");
}

export async function getEpisode(id) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
        return await apiFetch(`/episodes/${id}`, { signal: controller.signal });
    } catch (error) {
        if (controller.signal.aborted) {
            throw new Error("Loading episode details timed out. Please try again.");
        }
        throw error;
    } finally {
        clearTimeout(timer);
    }
}

export async function createEpisodeShare(episodeId, emails) {
    const result = await apiFetch(`/episodes/${encodeURIComponent(episodeId)}/share`, {
        method: "POST",
        body: JSON.stringify({
            emails: emails || "",
        }),
    });
    return validateEpisodeShareResult(result);
}

export async function sendEpisodeShareEmails(shareToken, emails) {
    return apiFetch(`/episodes/share/${encodeURIComponent(shareToken)}/send-email`, {
        method: "POST",
        body: JSON.stringify({
            emails: emails || "",
        }),
    });
}

export async function getPublicEpisodeShare(token) {
    const res = await fetch(
        `${API_URL}/episodes/share/public/${encodeURIComponent(String(token || ""))}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function listReviewFiles(token, path = "/") {
    const qs = new URLSearchParams({ path: path || "/" });
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(
            String(token || "")
        )}/files?${qs.toString()}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function listReviewMarkers(token) {
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(String(token || ""))}/markers`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function createReviewMarker(token, payload = {}) {
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(String(token || ""))}/markers`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload || {}),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function deleteReviewMarker(token, markerId) {
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(
            String(token || "")
        )}/markers/${encodeURIComponent(String(markerId || ""))}`,
        {
            method: "DELETE",
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function createReviewShare(token, emails) {
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(String(token || ""))}/share`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                emails: emails || "",
            }),
        }
    );
    const result = await res.json().catch(() => ({ ok: false, message: "Invalid response" }));
    return validateEpisodeShareResult(result);
}

export async function sendReviewShareEmails(token, shareToken, emails) {
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(
            String(token || "")
        )}/share/${encodeURIComponent(String(shareToken || ""))}/send-email`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                emails: emails || "",
            }),
        }
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export function buildReviewMarkerStreamUrl(token) {
    return `${API_URL}/review/${encodeURIComponent(
        String(token || "")
    )}/markers/stream`;
}

export async function getReviewFileStreamUrl(token, itemId) {
    const qs = new URLSearchParams({ itemId: String(itemId || "") });
    const res = await fetch(
        `${API_URL}/review/${encodeURIComponent(
            String(token || "")
        )}/files/stream-url?${qs.toString()}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export function buildReviewFileStreamProxyUrl(token, itemId) {
    const qs = new URLSearchParams({ itemId: String(itemId || "") });
    return `${API_URL}/review/${encodeURIComponent(
        String(token || "")
    )}/files/stream?${qs.toString()}`;
}

export async function listPublicEpisodeShareFiles(token, path = "/") {
    const qs = new URLSearchParams({ path: path || "/" });
    const res = await fetch(
        `${API_URL}/episodes/share/public/${encodeURIComponent(
            String(token || "")
        )}/files?${qs.toString()}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export async function getPublicEpisodeShareFileStreamUrl(token, itemId) {
    const qs = new URLSearchParams({ itemId: String(itemId || "") });
    const res = await fetch(
        `${API_URL}/episodes/share/public/${encodeURIComponent(
            String(token || "")
        )}/files/stream-url?${qs.toString()}`
    );
    return res.json().catch(() => ({ ok: false, message: "Invalid response" }));
}

export function buildPublicEpisodeShareFileStreamProxyUrl(token, itemId) {
    const qs = new URLSearchParams({ itemId: String(itemId || "") });
    return `${API_URL}/episodes/share/public/${encodeURIComponent(
        String(token || "")
    )}/files/stream?${qs.toString()}`;
}

export async function createEpisode(data) {
    return apiFetch("/episodes", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

export async function updateEpisode(id, data) {
    return apiFetch(`/episodes/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

export async function requestEpisodeChanges(id, suggestedChanges) {
    return apiFetch(`/episodes/${id}/change-requests`, {
        method: "POST",
        body: JSON.stringify({ suggested_changes: suggestedChanges }),
    });
}

export async function deleteEpisode(id) {
    return apiFetch(`/episodes/${id}`, {
        method: "DELETE",
    });
}

/* ============================================================
   EPISODE CHANGE HISTORY
   GET /episodes/:id/change-logs
============================================================ */

export async function getEpisodeChangeLogs(id) {
    return apiFetch(`/episodes/${id}/change-logs`);
}

export async function getGlobalChangeLogs(filters = {}) {
    const qs = new URLSearchParams();
    const source = String(filters.source || "").trim();
    const action = String(filters.action || "").trim();
    const changedBy = String(filters.changed_by_user_id || "").trim();
    const dateFrom = String(filters.date_from || "").trim();
    const dateTo = String(filters.date_to || "").trim();
    const search = String(filters.search || "").trim();
    const limit = String(filters.limit || "").trim();
    const page = String(filters.page || "").trim();
    const pageSize = String(filters.page_size || "").trim();

    if (source) qs.set("source", source);
    if (action) qs.set("action", action);
    if (changedBy) qs.set("changed_by_user_id", changedBy);
    if (dateFrom) qs.set("date_from", dateFrom);
    if (dateTo) qs.set("date_to", dateTo);
    if (search) qs.set("search", search);
    if (limit) qs.set("limit", limit);
    if (page) qs.set("page", page);
    if (pageSize) qs.set("page_size", pageSize);

    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch(`/episodes/change-logs/global${suffix}`);
}

export async function getAdminRecordingUsage(filters = {}) {
    const qs = new URLSearchParams();
    const search = String(filters.search || "").trim();
    const page = String(filters.page || "").trim();
    const pageSize = String(filters.page_size || "").trim();
    if (search) qs.set("search", search);
    if (page) qs.set("page", page);
    if (pageSize) qs.set("page_size", pageSize);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch(`/podcasts/recording-usage${suffix}`);
}

export async function getAdminBackgroundJobs({ status = "", search = "", page = 1, page_size = 25 } = {}) {
    const qs = new URLSearchParams({ page: String(page), page_size: String(page_size) });
    if (String(status || "").trim()) qs.set("status", String(status).trim());
    if (String(search || "").trim()) qs.set("search", String(search).trim());
    return apiFetch(`/settings/background-jobs?${qs.toString()}`);
}

export async function getEpisodeStorageLifecyclePreview({ page = 1, page_size = 25, search = "", outcome = "" } = {}) {
    const qs = new URLSearchParams({
        page: String(page || 1),
        page_size: String(page_size || 25),
    });
    if (String(search || "").trim()) qs.set("search", String(search).trim());
    if (String(outcome || "").trim()) qs.set("outcome", String(outcome).trim());
    return apiFetch(`/episodes/storage-lifecycle/preview?${qs.toString()}`);
}

export async function refreshEpisodeStorageLifecyclePreview() {
    return apiFetch("/episodes/storage-lifecycle/preview/refresh", { method: "POST" });
}

export async function runEpisodeArchiveNow() {
    return apiFetch("/episodes/storage-lifecycle/archive/run", { method: "POST" });
}

export async function archiveEpisodeStorageNow(episodeId) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/storage/archive`, { method: "POST" });
}

export async function retryAdminBackgroundJob(jobType, jobId) {
    return apiFetch(
        `/settings/background-jobs/${encodeURIComponent(jobType)}/${encodeURIComponent(jobId)}/retry`,
        { method: "POST" }
    );
}

export async function terminateAdminBackgroundJob(jobType, jobId) {
    return apiFetch(
        `/settings/background-jobs/${encodeURIComponent(jobType)}/${encodeURIComponent(jobId)}/terminate`,
        { method: "POST" }
    );
}

/* ============================================================
   MEDIA UPLOADS (FormData)
============================================================ */

export async function uploadEpisodeMedia(id, file, options = {}) {
    const formData = new FormData();
    formData.append("file", file);

    return apiUpload(`/episodes/${id}/media`, formData, options);
}

export async function uploadEpisodeArtwork(id, file, options = {}) {
    const formData = new FormData();
    formData.append("file", file);

    return apiUpload(`/episodes/${id}/artwork`, formData, options);
}

export async function useEpisodeFileAsAudio(id, itemId) {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/media/from-episode-file`, {
        method: "POST",
        body: JSON.stringify({
            item_id: String(itemId || "").trim(),
        }),
    });
}

export async function useEpisodeFileAsArtwork(id, itemId) {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/artwork/from-episode-file`, {
        method: "POST",
        body: JSON.stringify({
            item_id: String(itemId || "").trim(),
        }),
    });
}

export async function transcribeEpisodeAudio(id, itemId = "") {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/transcribe-audio`, {
        method: "POST",
        body: JSON.stringify({
            item_id: String(itemId || "").trim() || null,
        }),
    });
}

export async function retryEpisodeTranscriptCleanup(id) {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/retry-transcript-cleanup`, {
        method: "POST",
    });
}

export async function processEpisodeAudio(
    id,
    itemId = "",
    presetUuid = "",
    sourceFileName = "",
    options = {}
) {
    const processingOptions = options && typeof options === "object" ? options : {};
    return apiFetch(`/episodes/${encodeURIComponent(id)}/process-audio`, {
        method: "POST",
        body: JSON.stringify({
            item_id: String(itemId || "").trim() || null,
            preset_uuid: String(presetUuid || "").trim() || null,
            source_file_name: String(sourceFileName || "").trim() || null,
            output_file_name: String(processingOptions.output_file_name || "").trim() || null,
            destination_path: String(processingOptions.destination_path || "").trim() || null,
            delete_source_files: Boolean(processingOptions.delete_source_files),
        }),
    });
}

export async function processEpisodeAudioBatch(
    id,
    itemIds = [],
    presetUuid = "",
    options = {}
) {
    const processingOptions = options && typeof options === "object" ? options : {};
    const rawItems = Array.isArray(itemIds) ? itemIds : [];
    const itemConfigs = rawItems
        .filter((item) => item && typeof item === "object")
        .map((item) => ({
            item_id: String(item.item_id || item.id || "").trim(),
            preset_uuid: String(item.preset_uuid || "").trim() || null,
            output_file_name: String(item.output_file_name || "").trim() || null,
            destination_path: String(item.destination_path || "").trim() || null,
        }))
        .filter((item) => item.item_id);
    const normalizedItemIds = itemConfigs.length
        ? itemConfigs.map((item) => item.item_id)
        : rawItems
              .map((item) => String(item || "").trim())
              .filter(Boolean);
    return apiFetch(`/episodes/${encodeURIComponent(id)}/process-audio/bulk`, {
        method: "POST",
        body: JSON.stringify({
            item_ids: normalizedItemIds,
            ...(itemConfigs.length ? { items: itemConfigs } : {}),
            preset_uuid: String(presetUuid || "").trim() || null,
            delete_source_files: Boolean(processingOptions.delete_source_files),
        }),
    });
}

export async function getEpisodeAudioProcessingJobs(id) {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/audio-processing-jobs`);
}

export async function getEpisodeAuphonicEditor(id, jobId) {
    return apiFetch(
        `/episodes/${encodeURIComponent(id)}/audio-processing-jobs/${encodeURIComponent(jobId)}/auphonic-editor`
    );
}

export async function reprocessEpisodeAuphonic(id, jobId, payload = {}) {
    return apiFetch(
        `/episodes/${encodeURIComponent(id)}/audio-processing-jobs/${encodeURIComponent(jobId)}/auphonic-reprocess`,
        {
            method: "POST",
            body: JSON.stringify(payload || {}),
        }
    );
}

export async function acceptEpisodeAuphonicPreview(id, jobId, previewJobId) {
    return apiFetch(
        `/episodes/${encodeURIComponent(id)}/audio-processing-jobs/${encodeURIComponent(jobId)}/auphonic-preview/accept`,
        {
            method: "POST",
            body: JSON.stringify({ preview_job_id: previewJobId }),
        }
    );
}

export async function cancelEpisodeAuphonicPreview(id, jobId, previewJobId) {
    return apiFetch(
        `/episodes/${encodeURIComponent(id)}/audio-processing-jobs/${encodeURIComponent(jobId)}/auphonic-preview/cancel`,
        {
            method: "POST",
            body: JSON.stringify({ preview_job_id: previewJobId }),
        }
    );
}

export function buildAuphonicPreviewStreamUrl(id, jobId, previewJobId, outputIndex = 0) {
    const token = getToken();
    const qs = new URLSearchParams({
        preview_job_id: String(previewJobId || ""),
        output_index: String(outputIndex || 0),
        access_token: String(token || ""),
    });
    return `${API_URL}/episodes/${encodeURIComponent(id)}/audio-processing-jobs/${encodeURIComponent(jobId)}/auphonic-preview?${qs.toString()}`;
}

export async function processEpisodeMultitrack(id, payload) {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/process-multitrack`, {
        method: "POST",
        body: JSON.stringify(payload || {}),
    });
}

export async function generateEpisodeShowNotes(id) {
    return apiFetch(`/episodes/${encodeURIComponent(id)}/generate-show-notes`, {
        method: "POST",
    });
}

export async function getShowNotesSettings() {
    const data = await apiFetch("/settings/show-notes");
    if (!data.ok) throw new Error(data.message || "Failed to load show notes settings");
    return data.settings;
}

export async function updateShowNotesSettings(payload) {
    const data = await apiFetch("/settings/show-notes", {
        method: "PUT",
        body: JSON.stringify(payload),
    });
    if (!data.ok) throw new Error(data.message || "Failed to update show notes settings");
    return data.settings;
}

export async function getShowNotesModels(provider = "openai") {
    const resolvedProvider =
        String(provider || "").trim().toLowerCase() === "grok" ? "grok" : "openai";
    const data = await apiFetch(
        `/settings/show-notes/models?provider=${encodeURIComponent(resolvedProvider)}`
    );
    if (!data.ok) throw new Error(data.message || "Failed to load show notes models");
    return Array.isArray(data.models) ? data.models : [];
}

export async function getAuphonicPresets() {
    const data = await apiFetch("/settings/show-notes/auphonic-presets");
    if (!data.ok) throw new Error(data.message || "Failed to load Auphonic presets");
    return Array.isArray(data.presets) ? data.presets : [];
}

export async function getAuphonicPreset(presetUuid) {
    const safePresetUuid = String(presetUuid || "").trim();
    const data = await apiFetch(
        `/settings/show-notes/auphonic-presets/${encodeURIComponent(safePresetUuid)}`
    );
    if (!data.ok) throw new Error(data.message || "Failed to load Auphonic preset");
    return data.preset || null;
}

export async function getEpisodeNotificationMessageTemplates() {
    const data = await apiFetch("/settings/notification-message-templates");
    if (!data.ok) {
        throw new Error(
            data.message || "Failed to load notification message templates"
        );
    }
    return Array.isArray(data.templates) ? data.templates : [];
}

export async function createEpisodeNotificationMessageTemplate(payload) {
    const data = await apiFetch("/settings/notification-message-templates", {
        method: "POST",
        body: JSON.stringify(payload),
    });
    if (!data.ok) {
        throw new Error(
            data.message || "Failed to create notification message template"
        );
    }
    return Array.isArray(data.templates) ? data.templates : [];
}

export async function updateEpisodeNotificationMessageTemplate(id, payload) {
    const data = await apiFetch(
        `/settings/notification-message-templates/${encodeURIComponent(id)}`,
        {
            method: "PUT",
            body: JSON.stringify(payload),
        }
    );
    if (!data.ok) {
        throw new Error(
            data.message || "Failed to update notification message template"
        );
    }
    return Array.isArray(data.templates) ? data.templates : [];
}

export async function deleteEpisodeNotificationMessageTemplate(id) {
    const data = await apiFetch(
        `/settings/notification-message-templates/${encodeURIComponent(id)}`,
        { method: "DELETE" }
    );
    if (!data.ok) {
        throw new Error(
            data.message || "Failed to delete notification message template"
        );
    }
    return Array.isArray(data.templates) ? data.templates : [];
}

export async function reorderEpisodeNotificationMessageTemplates(orderedIds) {
    const data = await apiFetch("/settings/notification-message-templates/order", {
        method: "PUT",
        body: JSON.stringify({ ordered_ids: orderedIds }),
    });
    if (!data.ok) {
        throw new Error(
            data.message || "Failed to reorder notification message templates"
        );
    }
    return Array.isArray(data.templates) ? data.templates : [];
}

// ------------------------------------------------------------
// Backwards compatibility for older code using fetchProtected()
// ------------------------------------------------------------
export async function fetchProtected(url, options = {}) {
    return apiFetch(url, options);
}

/* ============================================================
   EPISODE NOTIFICATIONS
============================================================ */

export async function notifyEpisode(id, message) {
    return apiFetch(`/episodes/${id}/notify`, {
        method: "POST",
        body: JSON.stringify({ message }),
    });
}

/* ============================================================
   GET HEALTH STATUS
============================================================ */

export async function getHealth() {
    try {
        const res = await fetch(`${API_URL}/health`);
        const data = await res.json().catch(() => null);

        if (!res.ok || !data) {
            return { ok: false, status: "down" };
        }

        return data; // { ok: true/false, status: "operational" | "down" }
    } catch (err) {
        console.error("Health API error:", err);
        return { ok: false, status: "down" };
    }
}

/* ============================================================
   SHAREPOINT FILE EXPLORER
============================================================ */

export async function listSharePointItems(path = "/") {
    const qs = new URLSearchParams({ path });
    return apiFetch(`/sharepoint/list?${qs.toString()}`);
}

export async function uploadSharePointFile(path, file, options = {}) {
    if (Number(file?.size || 0) > LARGE_SHAREPOINT_UPLOAD_THRESHOLD_BYTES) {
        try {
            return await uploadLargeSharePointFile({
                createSessionPath: "/sharepoint/upload-session",
                finalizePath: "/sharepoint/upload-finalize",
                path,
                file,
                onProgress: options.onProgress,
            });
        } catch (err) {
            return {
                ok: false,
                message: err?.message || "Large file upload failed",
            };
        }
    }

    const formData = new FormData();
    formData.append("path", path || "/");
    formData.append("file", file);

    return apiUpload("/sharepoint/upload", formData, options);
}

export async function downloadSharePointFile(itemId, requestedFileName = "") {
    const token = getToken();
    const qs = new URLSearchParams({ itemId: String(itemId) });
    if (requestedFileName && String(requestedFileName).trim()) {
        qs.set("fileName", String(requestedFileName).trim());
    }

    const res = await fetch(`${API_URL}/sharepoint/download?${qs.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (res.status === 401) {
        handleAuthFailure();
        return { ok: false, message: "Invalid or expired token" };
    }

    if (!res.ok) {
        const text = await res.text().catch(() => "");
        return {
            ok: false,
            message: text || "Failed to download file",
        };
    }

    const blob = await res.blob();
    const disposition = res.headers.get("content-disposition") || "";
    const encodedMatch = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    const quotedMatch = disposition.match(/filename="([^"]+)"/i);
    const bareMatch = disposition.match(/filename=([^;]+)/i);

    let resolvedFileName = "";
    if (encodedMatch?.[1]) {
        resolvedFileName = decodeURIComponent(encodedMatch[1]).trim();
    } else if (quotedMatch?.[1]) {
        resolvedFileName = quotedMatch[1].trim();
    } else if (bareMatch?.[1]) {
        resolvedFileName = bareMatch[1].trim();
    }

    return { ok: true, blob, fileName: resolvedFileName };
}

export async function getSharePointFileStreamUrl(itemId) {
    const qs = new URLSearchParams({ itemId: String(itemId || "") });
    return apiFetch(`/sharepoint/item-stream-url?${qs.toString()}`);
}

export function buildSharePointFileStreamProxyUrl(itemId) {
    const token = getToken();
    const qs = new URLSearchParams({
        itemId: String(itemId || ""),
        access_token: String(token || ""),
    });
    return `${API_URL}/sharepoint/item-stream?${qs.toString()}`;
}

export async function createSharePointFolder(path, name) {
    return apiFetch("/sharepoint/create-folder", {
        method: "POST",
        body: JSON.stringify({
            path: path || "/",
            name,
        }),
    });
}

export async function deleteSharePointItem(itemId) {
    return apiFetch(`/sharepoint/item/${encodeURIComponent(itemId)}`, {
        method: "DELETE",
    });
}

export async function renameSharePointItem(itemId, newName, targetFolderItemId = "") {
    return apiFetch(`/sharepoint/item/${encodeURIComponent(itemId)}/rename`, {
        method: "PATCH",
        body: JSON.stringify({
            new_name: newName || null,
            target_folder_item_id: targetFolderItemId || null,
        }),
    });
}

export async function setSharePointEpisodeOverride(path, episodeCode) {
    return apiFetch("/sharepoint/episode-override", {
        method: "POST",
        body: JSON.stringify({
            path: path || "/",
            episode_code: episodeCode || "",
        }),
    });
}

export async function clearSharePointEpisodeOverride(path) {
    const qs = new URLSearchParams({ path: path || "/" });
    return apiFetch(`/sharepoint/episode-override?${qs.toString()}`, {
        method: "DELETE",
    });
}

/* ============================================================
   EPISODE SCOPED SHAREPOINT FILE EXPLORER
============================================================ */

export async function listEpisodeFiles(episodeId, path = "/") {
    const qs = new URLSearchParams({ path: path || "/" });
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/files?${qs.toString()}`);
}

export async function listEpisodeFileFolders(episodeId) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/files/folders`);
}

export async function restoreEpisodeFiles(episodeId) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/storage/restore`, {
        method: "POST",
    });
}

export function buildEpisodePermanentMediaUrl(episodeId, role) {
    const token = getToken();
    const qs = token ? `?access_token=${encodeURIComponent(token)}` : "";
    return `${API_URL}/episodes/${encodeURIComponent(episodeId)}/media/${encodeURIComponent(role)}${qs}`;
}

export async function uploadEpisodeFile(episodeId, path, file, options = {}) {
    if (Number(file?.size || 0) > LARGE_SHAREPOINT_UPLOAD_THRESHOLD_BYTES) {
        try {
            return await uploadLargeSharePointFile({
                createSessionPath: `/episodes/${encodeURIComponent(episodeId)}/files/upload-session`,
                finalizePath: `/episodes/${encodeURIComponent(episodeId)}/files/upload-finalize`,
                path,
                file,
                conflictBehavior: options.conflictBehavior || "rename",
                onProgress: options.onProgress,
            });
        } catch (err) {
            return {
                ok: false,
                code: Number(err?.status) === 409 ? "file_name_conflict" : undefined,
                message: err?.message || "Large file upload failed",
            };
        }
    }

    const formData = new FormData();
    formData.append("path", path || "/");
    formData.append("file", file);
    formData.append(
        "conflict_behavior",
        options.conflictBehavior || "rename"
    );

    return apiUpload(
        `/episodes/${encodeURIComponent(episodeId)}/files/upload`,
        formData,
        options
    );
}

export async function completeEpisodeFileUploadBatch(episodeId, payload = {}) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/files/upload-complete`, {
        method: "POST",
        body: JSON.stringify(payload || {}),
    });
}

export async function downloadEpisodeFile(
    episodeId,
    itemId,
    requestedFileName = ""
) {
    const token = getToken();
    const qs = new URLSearchParams({ itemId: String(itemId) });
    if (requestedFileName && String(requestedFileName).trim()) {
        qs.set("fileName", String(requestedFileName).trim());
    }

    const res = await fetch(
        `${API_URL}/episodes/${encodeURIComponent(
            episodeId
        )}/files/download?${qs.toString()}`,
        {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
    );

    if (res.status === 401) {
        handleAuthFailure();
        return { ok: false, message: "Invalid or expired token" };
    }

    if (!res.ok) {
        const text = await res.text().catch(() => "");
        return { ok: false, message: text || "Failed to download file" };
    }

    const blob = await res.blob();
    const disposition = res.headers.get("content-disposition") || "";
    const encodedMatch = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    const quotedMatch = disposition.match(/filename="([^"]+)"/i);
    const bareMatch = disposition.match(/filename=([^;]+)/i);

    let resolvedFileName = "";
    if (encodedMatch?.[1]) {
        resolvedFileName = decodeURIComponent(encodedMatch[1]).trim();
    } else if (quotedMatch?.[1]) {
        resolvedFileName = quotedMatch[1].trim();
    } else if (bareMatch?.[1]) {
        resolvedFileName = bareMatch[1].trim();
    }

    return { ok: true, blob, fileName: resolvedFileName };
}

async function prepareEpisodeZipDownload(episodeId, payload) {
    const result = await apiFetch(
        `/episodes/${encodeURIComponent(episodeId)}/files/download-zip-ticket`,
        { method: "POST", body: JSON.stringify(payload) }
    );
    if (!result?.ok || !result.ticket) return result;
    return {
        ok: true,
        downloadUrl: `${API_URL}/episodes/${encodeURIComponent(episodeId)}/files/download-zip?ticket=${encodeURIComponent(result.ticket)}`,
    };
}

export function downloadSelectedEpisodeFiles(episodeId, itemIds) {
    return prepareEpisodeZipDownload(episodeId, { kind: "selected", item_ids: itemIds });
}

export function downloadEpisodeFolderFiles(episodeId, folderId) {
    return prepareEpisodeZipDownload(episodeId, { kind: "folder", folder_id: folderId });
}

export async function getEpisodeFileStreamUrl(episodeId, itemId) {
    const qs = new URLSearchParams({ itemId: String(itemId || "") });
    return apiFetch(
        `/episodes/${encodeURIComponent(episodeId)}/files/stream-url?${qs.toString()}`
    );
}

export function buildEpisodeFileStreamProxyUrl(episodeId, itemId) {
    const token = getToken();
    const qs = new URLSearchParams({
        itemId: String(itemId || ""),
        access_token: String(token || ""),
    });
    return `${API_URL}/episodes/${encodeURIComponent(episodeId)}/files/stream?${qs.toString()}`;
}

export async function createEpisodeFolder(episodeId, path, name) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/files/create-folder`, {
        method: "POST",
        body: JSON.stringify({
            path: path || "/",
            name,
        }),
    });
}

export async function deleteEpisodeFileItem(episodeId, itemId) {
    return apiFetch(
        `/episodes/${encodeURIComponent(episodeId)}/files/item/${encodeURIComponent(itemId)}`,
        {
            method: "DELETE",
        }
    );
}

export async function deleteEpisodeFileItems(episodeId, itemIds = []) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/files/bulk-delete`, {
        method: "POST",
        body: JSON.stringify({
            item_ids: Array.isArray(itemIds) ? itemIds : [],
        }),
    });
}

export async function renameEpisodeFileItem(
    episodeId,
    itemId,
    newName,
    targetFolderItemId = ""
) {
    return apiFetch(
        `/episodes/${encodeURIComponent(
            episodeId
        )}/files/item/${encodeURIComponent(itemId)}/rename`,
        {
            method: "PATCH",
            body: JSON.stringify({
                new_name: newName || null,
                target_folder_item_id: targetFolderItemId || null,
            }),
        }
    );
}

export async function listEpisodeExternalLinks(episodeId) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/external-links`);
}

export async function createEpisodeExternalLink(episodeId, payload) {
    return apiFetch(`/episodes/${encodeURIComponent(episodeId)}/external-links`, {
        method: "POST",
        body: JSON.stringify({
            title: payload?.title ?? "",
            url: payload?.url ?? "",
            notes: payload?.notes ?? "",
        }),
    });
}

export async function updateEpisodeExternalLink(episodeId, linkId, payload) {
    return apiFetch(
        `/episodes/${encodeURIComponent(
            episodeId
        )}/external-links/${encodeURIComponent(linkId)}`,
        {
            method: "PATCH",
            body: JSON.stringify({
                title: payload?.title ?? "",
                url: payload?.url ?? "",
                notes: payload?.notes ?? "",
            }),
        }
    );
}

export async function deleteEpisodeExternalLink(episodeId, linkId) {
    return apiFetch(
        `/episodes/${encodeURIComponent(
            episodeId
        )}/external-links/${encodeURIComponent(linkId)}`,
        {
            method: "DELETE",
        }
    );
}

export async function searchPortal(query, { signal } = {}) {
    const qs = new URLSearchParams({ q: String(query || "") });
    return apiFetch(`/search?${qs.toString()}`, { signal });
}

export async function listPortalNotifications({ category = "all", status = "all", page = 1 } = {}) {
    const qs = new URLSearchParams({ category, status, page: String(page), page_size: "30" });
    return apiFetch(`/notifications?${qs.toString()}`);
}

export async function getPortalNotificationCount() {
    return apiFetch("/notifications/unread-count");
}

export async function markAllPortalNotificationsRead() {
    return apiFetch("/notifications/read-all", { method: "PATCH" });
}

export async function updatePortalNotification(id, action) {
    return apiFetch(`/notifications/${encodeURIComponent(id)}/${encodeURIComponent(action)}`, {
        method: "PATCH",
    });
}

export async function runPortalNotificationAction(id) {
    return apiFetch(`/notifications/${encodeURIComponent(id)}/action`, { method: "POST" });
}

export async function getPortalNotificationPreferences() {
    return apiFetch("/notifications/preferences");
}

export async function savePortalNotificationPreferences(preferences) {
    return apiFetch("/notifications/preferences", {
        method: "PUT",
        body: JSON.stringify({ preferences }),
    });
}

export async function createEpisodeSharePointFolder(episodeId) {
    return apiFetch(
        `/episodes/${encodeURIComponent(episodeId)}/sharepoint/create-folder`,
        {
            method: "POST",
        }
    );
}
